import { CustomFieldCheckedListDisplayItem } from '../api/CustomFieldCheckedListDisplayItem';
import { CustomFieldCheckedListInputItem } from '../api/CustomFieldCheckedListInputItem';
import { CustomFieldDateDisplayItem } from '../api/CustomFieldDateDisplayItem';
import { CustomFieldDateInputItem } from '../api/CustomFieldDateInputItem';
import { CustomFieldDisplayItem } from '../api/CustomFieldDisplayItem';
import { CustomFieldDropDownListDisplayItem } from '../api/CustomFieldDropDownListDisplayItem';
import { CustomFieldDropDownListInputItem } from '../api/CustomFieldDropDownListInputItem';
import { CustomFieldEmailDisplayItem } from '../api/CustomFieldEmailDisplayItem';
import { CustomFieldEmailInputItem } from '../api/CustomFieldEmailInputItem';
import { CustomFieldInputItem } from '../api/CustomFieldInputItem';
import { CustomFieldMultiLineDisplayItem } from '../api/CustomFieldMultiLineDisplayItem';
import { CustomFieldMultiLineInputItem } from '../api/CustomFieldMultiLineInputItem';
import { CustomFieldPolicyDisplayItem } from '../api/CustomFieldPolicyDisplayItem';
import { CustomFieldPolicyInputItem } from '../api/CustomFieldPolicyInputItem';
import { CustomFieldRadioListDisplayItem } from '../api/CustomFieldRadioListDisplayItem';
import { CustomFieldRadioListInputItem } from '../api/CustomFieldRadioListInputItem';
import { CustomFieldTextDisplayItem } from '../api/CustomFieldTextDisplayItem';
import { CustomFieldTextInputItem } from '../api/CustomFieldTextInputItem';
import { CustomFieldType } from '../api/Enums';

declare module '../api/CustomFieldDisplayItem' {
    namespace CustomFieldDisplayItem {
        let IsCheckedList: typeof isCheckedList;
        let IsDate: typeof isDate;
        let IsDropDownList: typeof isDropDownList;
        let IsEmail: typeof isEmail;
        let IsMultiLine: typeof isMultiLine;
        let IsPolicy: typeof isPolicy;
        let IsRadioList: typeof isRadioList;
        let IsText: typeof isText;

        let AsCheckedList: typeof asCheckedList;
        let AsDate: typeof asDate;
        let AsDropDownList: typeof asDropDownList;
        let AsEmail: typeof asEmail;
        let AsMultiLine: typeof asMultiLine;
        let AsPolicy: typeof asPolicy;
        let AsRadioList: typeof asRadioList;
        let AsText: typeof asText;

        let ConvertToInputItem: typeof convertToInputItem;
    }
}

CustomFieldDisplayItem.IsCheckedList = isCheckedList;
CustomFieldDisplayItem.IsDate = isDate;
CustomFieldDisplayItem.IsDropDownList = isDropDownList;
CustomFieldDisplayItem.IsEmail = isEmail;
CustomFieldDisplayItem.IsMultiLine = isMultiLine;
CustomFieldDisplayItem.IsPolicy = isPolicy;
CustomFieldDisplayItem.IsRadioList = isRadioList;
CustomFieldDisplayItem.IsText = isText;

CustomFieldDisplayItem.AsCheckedList = asCheckedList;
CustomFieldDisplayItem.AsDate = asDate;
CustomFieldDisplayItem.AsDropDownList = asDropDownList;
CustomFieldDisplayItem.AsEmail = asEmail;
CustomFieldDisplayItem.AsMultiLine = asMultiLine;
CustomFieldDisplayItem.AsPolicy = asPolicy;
CustomFieldDisplayItem.AsRadioList = asRadioList;
CustomFieldDisplayItem.AsText = asText;

CustomFieldDisplayItem.ConvertToInputItem = convertToInputItem;

function isCheckedList(item: CustomFieldDisplayItem): item is CustomFieldCheckedListDisplayItem {
    return item.FieldType === CustomFieldType.CheckedList;
}

function isDate(item: CustomFieldDisplayItem): item is CustomFieldDateDisplayItem {
    return item.FieldType === CustomFieldType.Date;
}

function isDropDownList(item: CustomFieldDisplayItem): item is CustomFieldDropDownListDisplayItem {
    return item.FieldType === CustomFieldType.DropDownList;
}

function isEmail(item: CustomFieldDisplayItem): item is CustomFieldEmailDisplayItem {
    return item.FieldType === CustomFieldType.Email;
}

function isMultiLine(item: CustomFieldDisplayItem): item is CustomFieldMultiLineDisplayItem {
    return item.FieldType === CustomFieldType.MultiLine;
}

function isPolicy(item: CustomFieldDisplayItem): item is CustomFieldPolicyDisplayItem {
    return item.FieldType === CustomFieldType.Policy;
}

function isRadioList(item: CustomFieldDisplayItem): item is CustomFieldRadioListDisplayItem {
    return item.FieldType === CustomFieldType.RadioList;
}

function isText(item: CustomFieldDisplayItem): item is CustomFieldTextDisplayItem {
    return item.FieldType === CustomFieldType.Text;
}

function asCheckedList(item: CustomFieldDisplayItem): CustomFieldCheckedListDisplayItem {
    if (isCheckedList(item)) { return item; }
    throw Error(`${CustomFieldDisplayItem.ClassName} is not a ${CustomFieldCheckedListDisplayItem.ClassName}.`);
}

function asDate(item: CustomFieldDisplayItem): CustomFieldDateDisplayItem {
    if (isDate(item)) { return item; }
    throw Error(`${CustomFieldDisplayItem.ClassName} is not a ${CustomFieldDateDisplayItem.ClassName}.`);
}

function asDropDownList(item: CustomFieldDisplayItem): CustomFieldDropDownListDisplayItem {
    if (isDropDownList(item)) { return item; }
    throw Error(`${CustomFieldDisplayItem.ClassName} is not a ${CustomFieldDropDownListDisplayItem.ClassName}.`);
}

function asEmail(item: CustomFieldDisplayItem): CustomFieldEmailDisplayItem {
    if (isEmail(item)) { return item; }
    throw Error(`${CustomFieldDisplayItem.ClassName} is not a ${CustomFieldEmailDisplayItem.ClassName}.`);
}

function asMultiLine(item: CustomFieldDisplayItem): CustomFieldMultiLineDisplayItem {
    if (isMultiLine(item)) { return item; }
    throw Error(`${CustomFieldDisplayItem.ClassName} is not a ${CustomFieldMultiLineDisplayItem.ClassName}.`);
}

function asPolicy(item: CustomFieldDisplayItem): CustomFieldPolicyDisplayItem {
    if (isPolicy(item)) { return item; }
    throw Error(`${CustomFieldDisplayItem.ClassName} is not a ${CustomFieldPolicyDisplayItem.ClassName}.`);
}

function asRadioList(item: CustomFieldDisplayItem): CustomFieldRadioListDisplayItem {
    if (isRadioList(item)) { return item; }
    throw Error(`${CustomFieldDisplayItem.ClassName} is not a ${CustomFieldRadioListDisplayItem.ClassName}.`);
}

function asText(item: CustomFieldDisplayItem): CustomFieldTextDisplayItem {
    if (isText(item)) { return item; }
    throw Error(`${CustomFieldDisplayItem.ClassName} is not a ${CustomFieldTextDisplayItem.ClassName}.`);
}

function convertToInputItem(field: CustomFieldDisplayItem): CustomFieldInputItem {
    if (CustomFieldDisplayItem.IsCheckedList(field)) {
        return new CustomFieldCheckedListInputItem({
            Required: field.Required,
            Options: [...field.Options]
        });
    } else if (CustomFieldDisplayItem.IsDate(field)) {
        return new CustomFieldDateInputItem({
            Required: field.Required,
            Hint: field.Hint
        });
    } else if (CustomFieldDisplayItem.IsDropDownList(field)) {
        return new CustomFieldDropDownListInputItem({
            Required: field.Required,
            Options: [...field.Options],
            Hint: field.Hint
        });
    } else if (CustomFieldDisplayItem.IsEmail(field)) {
        return new CustomFieldEmailInputItem({
            Required: field.Required,
            Hint: field.Hint
        });
    } else if (CustomFieldDisplayItem.IsMultiLine(field)) {
        return new CustomFieldMultiLineInputItem({
            Required: field.Required,
            Hint: field.Hint
        });
    } else if (CustomFieldDisplayItem.IsPolicy(field)) {
        return new CustomFieldPolicyInputItem({
            Required: field.Required,
            Url: !field.WebsiteMediaLibraryItemID && field.Url ? field.Url : null,
            Statement: field.Statement,
            WebsiteMediaLibraryItemID: field.WebsiteMediaLibraryItemID
        });
    } else if (CustomFieldDisplayItem.IsRadioList(field)) {
        return new CustomFieldRadioListInputItem({
            Options: [...field.Options]
        });
    } else if (CustomFieldDisplayItem.IsText(field)) {
        return new CustomFieldTextInputItem({
            Required: field.Required,
            Hint: field.Hint
        });
    }

    throw Error(`Unknown custom field type ${field.FieldType}`);
}
